import React from 'react';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const tagBg: Record<string, string> = { Feature: C.accent, Integration: C.ink, Legal: C.warning };

const items = [
  { tag: 'Feature', date: 'Jul 2026', title: 'AI Agent mode is now live', body: 'Opt-in automation books engineers, drafts tenant access notices and chases late rent for you — with a full approval log before anything is sent.' },
  { tag: 'Integration', date: 'Jun 2026', title: 'Revolut Business & Metro Bank added', body: 'MillOS now connects to Revolut Business and Metro Bank via read-only Open Banking, bringing supported UK banks to over 30.' },
  { tag: 'Legal', date: 'May 2026', title: "2028 EPC band C — what changes now", body: "We've updated every EPC workflow and the EPC Expiry Checker to flag properties that won't meet the proposed 2028 minimum band C." },
  { tag: 'Feature', date: 'Apr 2026', title: 'Portfolio compliance score, redesigned', body: 'The 0–100 score now breaks down by certificate type and shows the single next action that will most improve your score.' },
  { tag: 'Integration', date: 'Mar 2026', title: 'Xero & QuickBooks two-way sync', body: 'MillOS now pushes categorised income and expenses straight into Xero and QuickBooks, and pulls back reconciliation status.' },
  { tag: 'Feature', date: 'Feb 2026', title: 'Scoped accountant access', body: 'Give your accountant a free, role-scoped dashboard with a complete audit trail — no more emailing spreadsheets at year-end.' },
];

const NewsPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'News & Product Updates | MillOS',
    description: 'The latest from MillOS — new features, integrations, roadmap and UK property-law changes that affect landlords.',
    canonicalPath: '/news',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="What's new"
        title={<>MillOS <Box component="em" sx={{ fontStyle: 'italic' }}>News</Box></>}
        body="Product releases, new integrations and the UK property-law changes that matter."
        maxWidth={900}
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 820, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {items.map((n) => (
            <Box key={n.title} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px 1fr' }, gap: { xs: '12px', sm: '28px' }, background: C.panel, borderRadius: '24px', padding: { xs: '24px', md: '32px' } }}>
              <Box>
                <Box sx={{ display: 'inline-block', background: tagBg[n.tag] || C.accent, color: C.white, borderRadius: '32px', padding: '5px 13px', fontSize: 11, fontWeight: 600, letterSpacing: '0.05rem', textTransform: 'uppercase' }}>
                  {n.tag}
                </Box>
                <Box sx={{ marginTop: '12px', fontSize: 13, color: C.muted }}>{n.date}</Box>
              </Box>
              <Box>
                <Box component="h2" sx={{ fontFamily: F.display, fontSize: 26, lineHeight: 1.1, margin: 0 }}>{n.title}</Box>
                <Box sx={{ marginTop: '10px', color: C.body, fontSize: 15, lineHeight: 1.6 }}>{n.body}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default NewsPage;
